import React from "react";
import { Tooltip } from "antd";
import { dateConverter } from "../../../../helper/dateConverter";
import { replaceFormating } from "../../helper";
import { getRandomColorsBassedOnLetters } from "../../../../helper/colors";
import { ICONS } from "../../../../assets/icons";
import FlowReplyPreview from "./FlowReplyPreview";

// Message type components
import ImageMessagePreview from "./ImageMessagePreview";
import VideoMessagePreview from "./VideoMessagePreview";
import DocumentMessagePreview from "./DocumentMessagePreview";
import AudioMessagePreview from "./AudioMessagePreview";
import DocumentMessage from "./DocumentMessage";
import LocationMessage from "../../../../components/common/LocationMessage";
import MediaMessage from "../../../campaigns/components/sequence/preview/MediaMessage";

// Constants
const MAX_WORDS_TRUNCATE = 7;
const MESSAGE_TYPES = {
  LOCATION: "location",
  INTERACTIVE: "interactive",
  TEXT: "text",
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document",
  AUDIO: "audio",
  BUTTON: "button",
  CONTACTS: "contacts"
};

// Utility functions
const truncateText = (text, maxWords = MAX_WORDS_TRUNCATE) => {
  if (!text) return '';
  const words = text.split(' ');
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ') + '...';
};

const extractReplyContent = (replyMessage) => {
  if (!replyMessage) return { text: null, items: null };
  
  const text = replyMessage.template?.map(item => item?.text);
  const items = replyMessage.template?.map(item => item);
  
  return { text, items };
};

// Component for rendering reply message bar
const ReplyMessageBar = ({ replyMessage, replyText }) => {
  const { text: reply_message, items: previewContent } = extractReplyContent(replyMessage);
  const displayText = replyText || reply_message;
  
  if (!displayText) return null;

  return (
    <div className="w-full flex-row align-center bg-[var(--channelButton)] inbox12px-400 text-[var(--font-600)] b-radius-6 mb-10">
      <div className="h-[6vh] bg-[#6564CA] w-1" />
      <div className="flex flex-1 items-center justify-between">
        <div className="max-w-[20ch] truncate ml-10">
          {displayText}
        </div>
        {previewContent && (
          <div>
            {previewContent.map((item) => (
              <div key={item?.id}>
                {item?.format === "IMAGE" && (
                  <img
                    src={item?.example?.header_handle?.[0]}
                    className="w-[50px] h-[50px] object-cover rounded-sm ml-10 mr-10"
                    alt="Reply preview"
                  />
                )}
                {item?.format === "VIDEO" && (
                  <video className="w-[50px] h-[50px] object-cover rounded-sm ml-10 mr-10">
                    <source src={item?.example?.header_handle?.[0]} type="video/mp4" />
                  </video>
                )}
                {item?.format === "DOCUMENT" && (
                  <iframe
                    className="w-[50px] h-[50px] object-cover rounded-sm ml-10 mr-10"
                    src={item?.example?.header_handle?.[0]}
                    title="Document preview"
                  />
                )}
                {item?.format === "AUDIO" && (
                  <audio className="w-[50px] h-[50px] object-cover rounded-sm ml-10 mr-10">
                    <source src={item?.example?.header_handle?.[0]} type="audio/mp3" />
                  </audio>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Component for rendering message timestamp
const MessageTimestamp = ({ timestamp }) => (
  <div className="flex justify-end text-[0.55vw] weight-semibold text-[var(--font-400)] mt-[0.3vw] ml-40">
    <p>{timestamp}</p>
  </div>
);

// Component for rendering user avatar
const UserAvatar = ({ userName, userPhoto }) => {
  const firstLetter = userName?.slice(0, 1).toUpperCase() || "";
  const colors = getRandomColorsBassedOnLetters(firstLetter);

  return (
    <Tooltip title={userName}>
      {userPhoto ? (
        <img
          src={userPhoto}
          alt={userName || "User"}
          className="rounded-full w-[15%] ml-5 pointer"
        />
      ) : (
        userName && (
          <span
            className="size-8 min-w-8 text-[0.8rem] border-2 border-white rounded-full text-[var(--font-600)] text-base weight-semibold flex items-center capitalize justify-center pointer bg-gray-100"
            style={{
              color: colors.color,
              backgroundColor: colors.backgroundColor,
            }}
          >
            {firstLetter}
          </span>
        )
      )}
    </Tooltip>
  );
};

// Component for rendering action icons
const ActionIcons = ({ onReply, messageId, isChatResolved }) => {
  if (isChatResolved) return null;

  return (
    <div className="action-icons-left flex items-center pl-2">
      <img
        src={ICONS?.replyIcon}
        onClick={() => onReply(messageId)}
        className="pointer w-[18px] h-[18px]"
        alt="Reply"
      />
    </div>
  );
};

// Main message content renderer
const MessageContent = ({ data, timestamp }) => {
  const messageType = data?.message?.type;
  const message = data?.message;
  const { text: reply_message, items: previewContent } = extractReplyContent(data?.reply_message);

  // Helper function to get media URLs
  const getMediaUrls = (format) => {
    return previewContent?.map(item => 
      item?.format === format && item?.example?.header_handle?.[0]
    ).filter(Boolean);
  };

  switch (messageType) {
    case MESSAGE_TYPES.LOCATION:
      return (
        <LocationMessage
          location={message.location}
          replyText={data?.reply_message?.interactive?.body?.text}
          timestamp={timestamp}
        />
      );

    case MESSAGE_TYPES.INTERACTIVE:
      return (
        <div>
          <ReplyMessageBar 
            replyMessage={data?.reply_message} 
            replyText={data?.reply_message?.interactive?.body?.text}
          />
          {message?.interactive?.type === "nfm_reply" ? (
            <FlowReplyPreview
              flowReply={message?.interactive?.nfm_reply}
              timestamp={timestamp}
            />
          ) : (
            <>
              <div className="new-body-text rounded-[0.6vw] rounded-tl-none">
                {message?.interactive?.button_reply?.title || message?.interactive?.list_reply?.title}
              </div>
              <div className="w-full break-words h-full new-body-text rounded-[0.6vw] rounded-tl-none">
                <div
                  dangerouslySetInnerHTML={{
                    __html: replaceFormating(message?.text?.body),
                  }}
                />
                <MessageTimestamp timestamp={timestamp} />
              </div>
            </>
          )}
        </div>
      );

    case MESSAGE_TYPES.IMAGE:
      return (
        <ImageMessagePreview
          content={reply_message}
          urls={getMediaUrls("IMAGE")}
          url={message?.image?.link}
          caption={message?.image?.caption}
          messageId={data}
          dateTime={timestamp}
        />
      );

    case MESSAGE_TYPES.VIDEO:
      return (
        <VideoMessagePreview
          content={reply_message}
          urls={getMediaUrls("VIDEO")}
          url={message?.video?.link}
          caption={message?.video?.caption}
          dateTime={timestamp}
        />
      );

    case MESSAGE_TYPES.DOCUMENT:
      return (
        <DocumentMessage
          fileName={message?.document?.filename}
          fileSize={message?.document?.fileLength}
          fileType={message?.document?.type}
          fileUrl={message?.document?.link}
          fileCaption={message?.document?.caption}
          isOwn={data?.is_customer_msg}
        />
      );

    case MESSAGE_TYPES.AUDIO:
      return (
        <AudioMessagePreview
          content={reply_message}
          urls={getMediaUrls("AUDIO")}
          url={message?.audio?.link}
          caption={message?.audio?.caption}
          dateTime={timestamp}
        />
      );

    case MESSAGE_TYPES.CONTACTS:
      return (
        <MediaMessage
          previewMode={true}
          node={{
            type: message?.interactive?.type?.toUpperCase() ?? "CONTACTS",
            data: message,
          }}
          allowToShowEdge={false}
          rootClassName="bg-transparent"
        />
      );

    case MESSAGE_TYPES.BUTTON:
      return (
        <div>
          <ReplyMessageBar 
            replyMessage={data?.reply_message}
            replyText={data?.reply_message?.text?.body}
          />
          <div className="new-body-text rounded-[0.6vw] rounded-tl-none">
            {message?.button?.text}
          </div>
          <MessageTimestamp timestamp={timestamp} />
        </div>
      );

    case MESSAGE_TYPES.TEXT:
    default:
      return (
        <div>
          {/* Referral content */}
          {message?.referral && (
            <div className="w-full bg-[var(--channelButton)]">
              <div className="flex items-start">
                {message?.image?.link && (
                  <img
                    src={message.image.link}
                    alt="Referral"
                    className="w-[50px] h-[50px] object-cover rounded-sm mr-10"
                  />
                )}
                <div className="flex-1">
                  <div className="font-semibold text-[0.7rem] mb-1 truncate max-w-[200px]">
                    {truncateText(message.referral.headline)}
                  </div>
                  <div className="text-[0.6rem] text-gray-600 mb-1 line-clamp-2">
                    {message.referral.body}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Reply message bar */}
          <ReplyMessageBar 
            replyMessage={data?.reply_message}
            replyText={data?.reply_message?.text?.body}
          />
          
          {/* Main message content */}
          {(message?.text?.body || message?.body) && (
            <div className="w-full break-words h-full new-body-text rounded-[0.6vw] rounded-tl-none">
              <div
                className="inbox-formatted-message"
                dangerouslySetInnerHTML={{
                  __html: replaceFormating(message?.text?.body || message?.body),
                }}
              />
              <MessageTimestamp timestamp={timestamp} />
            </div>
          )}
        </div>
      );
  }
};

// Main component
export default function LeftSideConversationCard({
  data,
  handleReply,
  handleCopy,
  isChatResolved,
}) {
  const timestamp = dateConverter(data?.created_at, false);
  const messageType = data?.message?.type;
  
  // Determine if message needs special container styling
  const needsSpecialContainer = [
    MESSAGE_TYPES.TEXT,
    MESSAGE_TYPES.BUTTON,
    MESSAGE_TYPES.INTERACTIVE
  ].includes(messageType) && !data?.message?.referral;

  const containerClass = needsSpecialContainer 
    ? "bg-white p-[0.5vw] rounded-[0.6vw] rounded-tl-none"
    : "bg-white b-radius-6 p-[0.8vw]";

  return (
    <div className="w-full min-h-[3.5vh] h-full flex items-center">
      <div className="h-fit flex-row relative w-fit max-w-[25vw] single-conversation-container">
        <div className="flex-column">
          <div className={containerClass}>
            <MessageContent 
              data={data} 
              timestamp={timestamp?.timeWithMeridiem}
            />
          </div>

          <UserAvatar 
            userName={data?.user_name}
            userPhoto={data?.user_profile_photo}
          />
        </div>

        <ActionIcons
          onReply={handleReply}
          messageId={data?.id}
          isChatResolved={isChatResolved}
        />
      </div>
    </div>
  );
}
